import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import Pagination from '../../components/Pagination/Pagination';
import membershipService from '../../services/membershipService';
import { API_URL } from '../../config/api';
import './AdminVerificationManagePage.css';

const AdminVerificationManagePage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sellers, setSellers] = useState([]);
  const [verificationCounts, setVerificationCounts] = useState({ verified: 0, inspected: 0, certified: 0, pending: 0, declined: 0 });
  const [loading, setLoading] = useState(true);
  const [viewingDocs, setViewingDocs] = useState(null);
  const [changingLevel, setChangingLevel] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('');
  const ITEMS_PER_PAGE = 10;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const statusMap = { 
        all: 'all', 
        verified: 'verified',
        inspected: 'inspected',
        certified: 'certified',
        pending: 'pending', 
        declined: 'declined'
      };
      const data = await membershipService.getAdminVerificationRequests({
        status: statusMap[activeTab] || 'all',
        search: searchQuery
      });
      setSellers(data.requests || []);
      setVerificationCounts(data.counts || { verified: 0, inspected: 0, certified: 0, pending: 0, declined: 0 });
    } catch (error) {
      console.error('Error fetching verification data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const verificationTypes = [
    { name: 'Verified', price: '$99/year', description: 'Document verification only', icon: '✓', color: '#29ABE2', count: verificationCounts.verified },
    { name: 'Inspected', price: '$249/year', description: 'On-site inspection + documentation', icon: '🔍', color: '#F59E0B', count: verificationCounts.inspected },
    { name: 'Certified', price: '$499/year', description: 'Full verification package', icon: '🏆', color: '#10B981', count: verificationCounts.certified },
  ];

  const getLevelClass = (level) => {
    switch (level) {
      case 'verified': return 'level-verified';
      case 'inspected': return 'level-inspected';
      case 'certified': return 'level-certified';
      case 'declined': return 'level-declined';
      default: return 'level-pending';
    }
  };

  const getLevelLabel = (level) => {
    switch (level) {
      case 'verified': return 'Verified';
      case 'inspected': return 'Inspected';
      case 'certified': return 'Certified';
      case 'declined': return 'Declined';
      default: return 'Pending';
    }
  };

  const handleChangeLevel = async () => {
    if (!changingLevel || !selectedLevel) return;
    try {
      await membershipService.updateSellerVerification(changingLevel.sellerId, selectedLevel);
      setChangingLevel(null);
      setSelectedLevel('');
      fetchData();
    } catch (error) {
      alert('Failed to update: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleQuickApprove = async (seller, level) => {
    if (!window.confirm(`Set ${seller.company} verification to "${level}"?`)) return;
    try {
      await membershipService.updateSellerVerification(seller.sellerId, level);
      fetchData();
    } catch (error) {
      alert('Failed to update: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleViewDocs = (seller) => {
    setViewingDocs(seller);
  };

  const getDocumentLabel = (key) => {
    const labels = {
      businessRegistration: 'Business Registration Certificate',
      tradeVATCertificate: 'Trade or VAT Certificate',
      companyOwnerID: 'Company Owner/Director ID',
      businessLicense: 'Business License',
      companyAddress: 'Proof of Company Address'
    };
    return labels[key] || key;
  };

  const getBackendUrl = () => {
    return API_URL.replace('/api', '');
  };

  const paginatedSellers = sellers.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <AdminLayout>
      <div className="admin-verification-content">
        <div className="verification-page-header">
          <div className="verification-title-section">
            <h1 className="verification-page-title">Verification Management</h1>
            <p className="verification-page-description">Review and manage supplier verification requests</p>
          </div>
        </div>

        {/* Verification Types Cards */}
        <div className="verification-types-grid">
          {verificationTypes.map((type, index) => (
            <div key={index} className={`verification-type-card ${type.name.toLowerCase()}`}>
              <div className="type-icon">{type.icon}</div>
              <div className="type-info">
                <h3 className="type-name">{type.name}</h3>
                <p className="type-price">{type.price}</p>
                <p className="type-description">{type.description}</p>
              </div>
              <div className="type-count">{type.count} Active</div>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div className="verification-filters">
          <div className="filter-tabs">
            <button className={`filter-tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => { setActiveTab('all'); setCurrentPage(1); }}>All Suppliers ({verificationCounts.verified + verificationCounts.inspected + verificationCounts.certified + verificationCounts.pending + verificationCounts.declined})</button>
            <button className={`filter-tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => { setActiveTab('pending'); setCurrentPage(1); }}>Pending ({verificationCounts.pending})</button>
            <button className={`filter-tab ${activeTab === 'verified' ? 'active' : ''}`} onClick={() => { setActiveTab('verified'); setCurrentPage(1); }}>Verified ({verificationCounts.verified})</button>
            <button className={`filter-tab ${activeTab === 'inspected' ? 'active' : ''}`} onClick={() => { setActiveTab('inspected'); setCurrentPage(1); }}>Inspected ({verificationCounts.inspected})</button>
            <button className={`filter-tab ${activeTab === 'certified' ? 'active' : ''}`} onClick={() => { setActiveTab('certified'); setCurrentPage(1); }}>Certified ({verificationCounts.certified})</button>
            <button className={`filter-tab ${activeTab === 'declined' ? 'active' : ''}`} onClick={() => { setActiveTab('declined'); setCurrentPage(1); }}>Declined ({verificationCounts.declined})</button>
          </div>
          <div className="verification-search">
            <input 
              type="text" 
              placeholder="Search by company..." 
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        {/* Sellers Table */}
        <div className="verification-table-container">
          {loading ? (
            <div className="verification-loading">Loading suppliers...</div>
          ) : paginatedSellers.length === 0 ? (
            <div className="verification-empty">No suppliers found.</div>
          ) : (
            <table className="verification-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Location</th>
                  <th>Products</th>
                  <th>Membership</th>
                  <th>Verification</th>
                  <th>Docs</th>
                  <th>Joined</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSellers.map((seller) => (
                  <tr key={seller.id}>
                    <td className="company-name">{seller.company}</td>
                    <td>{seller.email || '—'}</td>
                    <td>{seller.city ? `${seller.city}, ${seller.country}` : (seller.country || '—')}</td>
                    <td>{seller.products}</td>
                    <td><span className="membership-badge-text">{seller.membershipPlan}</span></td>
                    <td>
                      <span className={`verification-level-badge ${getLevelClass(seller.verificationLevel)}`}>
                        {getLevelLabel(seller.verificationLevel)}
                      </span>
                    </td>
                    <td>
                      {seller.documentCount > 0 ? (
                        <button className="action-btn view-btn" title="View Documents" onClick={() => handleViewDocs(seller)}>
                          {seller.documentCount} files
                        </button>
                      ) : '—'}
                    </td>
                    <td>{new Date(seller.createdAt).toLocaleDateString('en-CA')}</td>
                    <td>
                      <div className="action-buttons">
                        {seller.verificationLevel === 'pending' && (
                          <button className="action-btn approve-btn" title="Approve as Verified" onClick={() => handleQuickApprove(seller, 'verified')}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M13.3334 4L6.00008 11.3333L2.66675 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        )}
                        {seller.verificationLevel === 'pending' && (
                          <button className="action-btn reject-btn" title="Decline" onClick={() => handleQuickApprove(seller, 'declined')}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        )}
                        <button className="action-btn change-level-btn" title="Change Level" onClick={() => { setChangingLevel(seller); setSelectedLevel(seller.verificationLevel || 'pending'); }}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49654 12.4191 1.44775 12.6667 1.44775C12.9143 1.44775 13.1594 1.49654 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.38302 14.4088 2.61181C14.5035 2.84061 14.5523 3.08585 14.5523 3.33337C14.5523 3.5809 14.5035 3.82614 14.4088 4.05493C14.314 4.28373 14.1751 4.49162 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {sellers.length > ITEMS_PER_PAGE && (
          <Pagination currentPage={currentPage} totalPages={Math.ceil(sellers.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
        )}
      </div>

      {/* Document Viewer Modal */}
      {viewingDocs && (
        <div className="doc-viewer-overlay" onClick={() => setViewingDocs(null)}>
          <div className="doc-viewer-modal" onClick={e => e.stopPropagation()}>
            <h3>Submitted Documents</h3>
            <p className="doc-company">{viewingDocs.company} — {getLevelLabel(viewingDocs.verificationLevel)} Verification</p>
            <div className="doc-list">
              {Object.entries(viewingDocs.documents || {}).map(([key, filePath]) => (
                <div className="doc-item" key={key}>
                  <div className="doc-item-info">
                    <span className="doc-item-icon">📄</span>
                    <span className="doc-item-name">{getDocumentLabel(key)}</span>
                  </div>
                  <a href={`${getBackendUrl()}${filePath}`} target="_blank" rel="noopener noreferrer">View</a>
                </div>
              ))}
              {Object.keys(viewingDocs.documents || {}).length === 0 && (
                <p style={{ color: '#64748B', textAlign: 'center' }}>No documents uploaded</p>
              )}
            </div>
            <div className="doc-viewer-footer">
              <button className="doc-viewer-close" onClick={() => setViewingDocs(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Change Verification Level Modal */}
      {changingLevel && (
        <div className="doc-viewer-overlay" onClick={() => { setChangingLevel(null); setSelectedLevel(''); }}>
          <div className="doc-viewer-modal" onClick={e => e.stopPropagation()}>
            <h3>Change Verification Level</h3>
            <p className="doc-company">
              <strong>{changingLevel.company}</strong> — Currently: <span className={`verification-level-badge ${getLevelClass(changingLevel.verificationLevel)}`}>{getLevelLabel(changingLevel.verificationLevel)}</span>
            </p>
            <div style={{ margin: '20px 0' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: '#334155' }}>Select new level:</label>
              <select 
                value={selectedLevel} 
                onChange={(e) => setSelectedLevel(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '14px', color: '#1E293B' }}
              >
                <option value="pending">Pending</option>
                <option value="verified">Verified ($99/year)</option>
                <option value="inspected">Inspected ($249/year)</option>
                <option value="certified">Certified ($499/year)</option>
                <option value="declined">Declined</option>
              </select>
            </div>
            <div className="reject-modal-actions">
              <button className="reject-cancel-btn" onClick={() => { setChangingLevel(null); setSelectedLevel(''); }}>Cancel</button>
              <button className="reject-confirm-btn" style={{ backgroundColor: '#29ABE2' }} onClick={handleChangeLevel}>Update Level</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminVerificationManagePage;
